import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShoppingCart, 
  RefreshCw, 
  Mail, 
  Star, 
  Download, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Server
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/portal-orders`, { params });
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load portal orders");
    } finally {
      setLoading(false);
    }
  };

  const handleResendMagicLink = async (orderId) => {
    setActionLoading(prev => ({ ...prev, [orderId]: "magic_link" }));
    try {
      await axios.post(`${API}/portal-orders/${orderId}/resend-magic-link`);
      toast.success("Magic link sent successfully");
    } catch (error) {
      console.error("Failed to resend magic link:", error);
      toast.error(error.response?.data?.detail || "Failed to send magic link");
    } finally {
      setActionLoading(prev => ({ ...prev, [orderId]: null }));
      setShowActionDialog(false);
    }
  };

  const handleSendReviewEmail = async (orderId) => {
    setActionLoading(prev => ({ ...prev, [orderId]: "review" }));
    try {
      await axios.post(`${API}/portal-orders/${orderId}/send-review-email`);
      toast.success("Review request email sent");
      fetchOrders(); // Refresh to update review status
    } catch (error) {
      console.error("Failed to send review email:", error);
      toast.error(error.response?.data?.detail || "Failed to send review email");
    } finally {
      setActionLoading(prev => ({ ...prev, [orderId]: null }));
      setShowActionDialog(false);
    }
  };

  const openActionDialog = (order, type) => {
    setSelectedOrder(order);
    setActionType(type);
    setShowActionDialog(true);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry - now;
    
    if (diff <= 0) return "Expired";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: "outline", label: "Pending", icon: Clock },
      paid: { variant: "default", label: "Paid", icon: CheckCircle, className: "bg-green-600" },
      failed: { variant: "destructive", label: "Failed", icon: XCircle },
      refunded: { variant: "secondary", label: "Refunded", icon: RefreshCw }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getPortalStatusBadge = (status) => {
    const variants = {
      draft: { variant: "outline", label: "Draft" },
      preview_ready: { variant: "secondary", label: "Preview Ready" },
      awaiting_payment: { variant: "outline", label: "Awaiting Payment", className: "border-amber-500 text-amber-600" },
      paid: { variant: "default", label: "Paid", className: "bg-blue-600" },
      building_final: { variant: "default", label: "Building...", className: "bg-purple-600" },
      final_ready: { variant: "default", label: "Final Ready", className: "bg-green-600" }
    };
    const config = variants[status] || variants.draft;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const getReviewStatusBadge = (order) => {
    if (order.review_email_status === "sent") {
      return <Badge variant="outline" className="text-green-600 border-green-500">Sent</Badge>;
    }
    if (order.review_email_status === "scheduled") {
      return <Badge variant="outline" className="text-blue-600 border-blue-500">Scheduled</Badge>;
    }
    if (!order.review_opt_in) {
      return <Badge variant="outline" className="text-slate-400">Opted Out</Badge>;
    }
    return <Badge variant="outline">Not Sent</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart size={24} />
            Portal Orders
          </h1>
          <p className="text-muted-foreground">
            Manage customer self-serve portal purchases
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="orders-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchOrders} data-testid="orders-refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{orders.length}</div>
            <p className="text-sm text-muted-foreground">Total Orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {orders.filter(o => o.status === "paid").length}
            </div>
            <p className="text-sm text-muted-foreground">Paid</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">
              {orders.filter(o => o.hosting_selected).length}
            </div>
            <p className="text-sm text-muted-foreground">With Hosting</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">
              {orders.filter(o => o.status === "pending").length}
            </div>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
          <CardDescription>
            {orders.length} order{orders.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
              <p>No portal orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Portal Status</TableHead>
                    <TableHead>Hosting</TableHead>
                    <TableHead>Paid At</TableHead>
                    <TableHead>Download Expires</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                      <TableCell>
                        <div>
                          <Link 
                            to={`/admin/leads/${order.lead_id}`}
                            className="font-medium hover:underline text-blue-600"
                          >
                            {order.lead_info?.business_name || "Unknown"}
                          </Link>
                          <p className="text-xs text-muted-foreground">
                            {order.lead_info?.city}, {order.lead_info?.state}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{order.email}</TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        {getPortalStatusBadge(order.lead_info?.portal_status)}
                      </TableCell>
                      <TableCell>
                        {order.hosting_selected ? (
                          <Badge variant="default" className="bg-blue-600">
                            <Server size={12} className="mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(order.paid_at)}
                      </TableCell>
                      <TableCell>
                        {order.download_expires_at ? (
                          <div className="text-sm">
                            <span className={
                              new Date(order.download_expires_at) < new Date() 
                                ? "text-red-600" 
                                : "text-green-600"
                            }>
                              {getTimeRemaining(order.download_expires_at)}
                            </span>
                          </div>
                        ) : "—"}
                      </TableCell>
                      <TableCell>{getReviewStatusBadge(order)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openActionDialog(order, "magic_link")}
                            disabled={actionLoading[order.order_id]}
                            title="Resend magic link"
                          >
                            <Mail size={14} />
                          </Button>
                          {order.status === "paid" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openActionDialog(order, "review")}
                              disabled={actionLoading[order.order_id] || order.review_email_status === "sent"}
                              title="Send review request"
                            >
                              <Star size={14} />
                            </Button>
                          )}
                          {order.lead_id && (
                            <Link to={`/admin/leads/${order.lead_id}`}>
                              <Button size="sm" variant="ghost" title="View lead">
                                <ExternalLink size={14} />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "magic_link" ? "Resend Magic Link" : "Send Review Request"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "magic_link" ? (
                <>
                  This will send a new magic link to <strong>{selectedOrder?.email}</strong> 
                  so they can access their portal account.
                </>
              ) : (
                <>
                  This will send a review request email to <strong>{selectedOrder?.email}</strong> 
                  asking them to leave a Google review.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (actionType === "magic_link") {
                  handleResendMagicLink(selectedOrder?.order_id);
                } else {
                  handleSendReviewEmail(selectedOrder?.order_id);
                }
              }}
              disabled={actionLoading[selectedOrder?.order_id]}
            >
              {actionLoading[selectedOrder?.order_id] ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  {actionType === "magic_link" ? (
                    <><Mail size={16} className="mr-2" /> Send Link</>
                  ) : (
                    <><Star size={16} className="mr-2" /> Send Review</>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
